// src/pages/sqf/SqfTemplateBuilderPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import sqfService from '../../services/sqfService';
import sqfAiService from '../../services/sqfAiService';
import { useLanguage, t } from '../../context/LanguageContext';
import ConfirmModal from '../../components/ConfirmModal';

const CATEGORY_LABELS = {
  SOP: 'SOP', SSOP: 'SSOP', SPC: 'SPC', SCPC: 'SCPC', CCP: 'CCP', OTHER: 'Other',
};

const TRIGGER_LABELS = {
  scheduled:         'Scheduled (cron / time-based)',
  before_production: 'Before Production (gates baking page)',
  after_production:  'After Production (triggered on run close)',
  on_demand:         'On Demand (manual only)',
};

const FIELD_TYPES = [
  { value: 'number',      label: 'Number' },
  { value: 'pass_fail',   label: 'Pass / Fail' },
  { value: 'yes_no',      label: 'Yes / No' },
  { value: 'text',        label: 'Text / Notes' },
  { value: 'select',      label: 'Dropdown' },
  { value: 'radio',       label: 'Radio Buttons' },
  { value: 'multi_check', label: 'Multi-Checkbox' },
  { value: 'time',        label: 'Time' },
  { value: 'photo',       label: 'Photo' },
  { value: 'signature',   label: 'Signature' },
  { value: 'item_count_list', label: 'Item Count List (month-over-month)' },
  { value: 'visual_inspection', label: 'Visual Inspection (AI-assisted)' },
];

const DEFAULT_VISUAL_INSPECTION_CRITERIA = [
  { key: 'weight', label: 'Weight' },
  { key: 'ingredient_distribution', label: 'Ingredient Distribution' },
  { key: 'cooking', label: 'Cooking / Bake Color' },
  { key: 'roundness', label: 'Roundness / Shape' },
];

function uid() { return Math.random().toString(36).slice(2, 9); }

function defaultField() {
  return { id: 'f' + uid(), label: '', labelEs: '', type: 'text', required: false, helpText: '', helpTextEs: '', options: [], unit: '', validation: {} };
}

function defaultSection() {
  return { id: 's' + uid(), title: '', titleEs: '', fields: [defaultField()] };
}

// ─── Field Editor ─────────────────────────────────────────────────────────────

// selectableFields: all select/radio fields in the template (excluding this field),
// used to populate the "Link to field" dropdown on number fields.
function FieldEditor({ field, onChange, onDelete, selectableFields = [] }) {
  const { lang } = useLanguage();
  const update = (key, val) => onChange({ ...field, [key]: val });
  const updateValidation = (key, val) => onChange({ ...field, validation: { ...(field.validation || {}), [key]: val } });

  const updateOptionRange = (opt, rangeKey, val) => {
    const parsed = val === '' ? undefined : parseFloat(val);
    const existing = field.optionRanges?.[opt] || {};
    const updated = { ...existing };
    if (parsed === undefined) delete updated[rangeKey]; else updated[rangeKey] = parsed;
    const allRanges = { ...(field.optionRanges || {}), [opt]: updated };
    if (Object.keys(updated).length === 0) delete allRanges[opt];
    update('optionRanges', allRanges);
  };

  const cleanedOptions = (field.options || []).filter(Boolean);

  return (
    <div className="border rounded p-2 mb-2 bg-light">
      <div className="row g-2 mb-2">
        <div className="col">
          <input className="form-control form-control-sm" placeholder={t('Field label (EN) *', 'Etiqueta del campo (EN) *', lang)} value={field.label}
            onChange={e => update('label', e.target.value)} />
        </div>
        <div className="col">
          <input className="form-control form-control-sm" placeholder={t('Field label (ES)', 'Etiqueta del campo (ES)', lang)}
            value={field.labelEs || ''} onChange={e => update('labelEs', e.target.value)} />
        </div>
        <div className="col-auto">
          <select className="form-select form-select-sm" value={field.type} onChange={e => {
            const newType = e.target.value;
            if (newType === 'visual_inspection' && !(field.validation?.criteria?.length)) {
              onChange({ ...field, type: newType, validation: { ...(field.validation || {}), criteria: DEFAULT_VISUAL_INSPECTION_CRITERIA } });
            } else {
              update('type', newType);
            }
          }}>
            {FIELD_TYPES.map(ft => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
          </select>
        </div>
        <div className="col-auto">
          <button className="btn btn-outline-danger btn-sm" onClick={onDelete} title={t('Remove field', 'Eliminar campo', lang)}>×</button>
        </div>
      </div>

      <div className="row g-2 mb-1">
        <div className="col-auto">
          <div className="form-check form-check-inline">
            <input className="form-check-input" type="checkbox" id={`req-${field.id}`}
              checked={!!field.required} onChange={e => update('required', e.target.checked)} />
            <label className="form-check-label" style={{ fontSize: 12 }} htmlFor={`req-${field.id}`}>{t('Required', 'Requerido', lang)}</label>
          </div>
        </div>
        {field.type === 'number' && (
          <div className="col-auto">
            <input className="form-control form-control-sm" placeholder={t('Unit (e.g. °F, ppm)', 'Unidad (p. ej. °F, ppm)', lang)}
              style={{ width: 130 }} value={field.unit || ''} onChange={e => update('unit', e.target.value)} />
          </div>
        )}
      </div>

      {/* Number validation */}
      {field.type === 'number' && (
        <>
          {/* Link to a select/radio field for dynamic range */}
          {selectableFields.length > 0 && (
            <div className="d-flex align-items-center gap-2 mt-1">
              <span className="text-muted" style={{ fontSize: 11 }}>{t('Dynamic range from:', 'Rango dinámico desde:', lang)}</span>
              <select className="form-select form-select-sm" style={{ width: 'auto' }}
                value={field.linkedFieldId || ''}
                onChange={e => update('linkedFieldId', e.target.value || null)}>
                <option value="">{t('(none — use fixed range below)', '(ninguno — usar rango fijo abajo)', lang)}</option>
                {selectableFields.map(f => (
                  <option key={f.id} value={f.id}>{f.label || t('(unlabeled)', '(sin etiqueta)', lang)}</option>
                ))}
              </select>
            </div>
          )}
          {field.linkedFieldId && (
            <div className="form-text text-info" style={{ fontSize: 11 }}>
              {t(
                'Min/max will be set by the option selected in the linked field. Fixed range below is used as fallback if no range is configured for the selected option.',
                'El mín/máx será definido por la opción seleccionada en el campo vinculado. El rango fijo de abajo se usa como respaldo si no se configuró rango para la opción seleccionada.',
                lang
              )}
            </div>
          )}

          {/* Multiple readings toggle */}
          <div className="d-flex align-items-center gap-3 mt-1">
            <div className="form-check form-check-inline mb-0">
              <input className="form-check-input" type="checkbox" id={`mr-${field.id}`}
                checked={!!field.multipleReadings} onChange={e => update('multipleReadings', e.target.checked)} />
              <label className="form-check-label" style={{ fontSize: 11 }} htmlFor={`mr-${field.id}`}>{t('Multiple readings', 'Lecturas múltiples', lang)}</label>
            </div>
            {field.multipleReadings && (
              <div className="d-flex align-items-center gap-1">
                <span className="text-muted" style={{ fontSize: 11 }}>{t('Min required:', 'Mín requeridas:', lang)}</span>
                <input type="number" min={1} className="form-control form-control-sm" style={{ width: 60 }}
                  value={field.minReadings ?? 1}
                  onChange={e => update('minReadings', Math.max(1, parseInt(e.target.value, 10) || 1))} />
              </div>
            )}
          </div>
          {field.multipleReadings && (
            <div className="form-text" style={{ fontSize: 11 }}>
              {t(
                `At fill time the user starts with ${field.minReadings ?? 1} row${(field.minReadings ?? 1) !== 1 ? 's' : ''} and can add more. Empty rows beyond the minimum are ignored.`,
                `Al completar, el usuario comienza con ${field.minReadings ?? 1} fila${(field.minReadings ?? 1) !== 1 ? 's' : ''} y puede agregar más. Las filas vacías por encima del mínimo se ignoran.`,
                lang
              )}
            </div>
          )}

          {/* Fixed min/max */}
          <div className="row g-2 mt-1">
            <div className="col-auto d-flex align-items-center gap-1">
              <input type="number" className="form-control form-control-sm" placeholder={t('Min', 'Mínimo', lang)} style={{ width: 80 }}
                value={field.validation?.min ?? ''} onChange={e => updateValidation('min', e.target.value === '' ? undefined : parseFloat(e.target.value))} />
              <div className="form-check form-check-inline mb-0 ms-1">
                <input className="form-check-input" type="checkbox" id={`dib-${field.id}`}
                  checked={!!field.validation?.deviationIfBelow} onChange={e => updateValidation('deviationIfBelow', e.target.checked)} />
                <label className="form-check-label" style={{ fontSize: 11 }} htmlFor={`dib-${field.id}`}>{t('Dev if below', 'Dev si menor', lang)}</label>
              </div>
            </div>
            <div className="col-auto d-flex align-items-center gap-1">
              <input type="number" className="form-control form-control-sm" placeholder={t('Max', 'Máximo', lang)} style={{ width: 80 }}
                value={field.validation?.max ?? ''} onChange={e => updateValidation('max', e.target.value === '' ? undefined : parseFloat(e.target.value))} />
              <div className="form-check form-check-inline mb-0 ms-1">
                <input className="form-check-input" type="checkbox" id={`dia-${field.id}`}
                  checked={!!field.validation?.deviationIfAbove} onChange={e => updateValidation('deviationIfAbove', e.target.checked)} />
                <label className="form-check-label" style={{ fontSize: 11 }} htmlFor={`dia-${field.id}`}>{t('Dev if above', 'Dev si mayor', lang)}</label>
              </div>
            </div>
          </div>
        </>
      )}

      {/* yes_no deviation */}
      {field.type === 'yes_no' && (
        <div className="d-flex gap-3 mt-1">
          <div className="form-check">
            <input className="form-check-input" type="checkbox" id={`diy-${field.id}`}
              checked={!!field.validation?.deviationIfYes} onChange={e => updateValidation('deviationIfYes', e.target.checked)} />
            <label className="form-check-label" style={{ fontSize: 11 }} htmlFor={`diy-${field.id}`}>{t('Dev if Yes', 'Dev si Sí', lang)}</label>
          </div>
          <div className="form-check">
            <input className="form-check-input" type="checkbox" id={`din-${field.id}`}
              checked={!!field.validation?.deviationIfNo} onChange={e => updateValidation('deviationIfNo', e.target.checked)} />
            <label className="form-check-label" style={{ fontSize: 11 }} htmlFor={`din-${field.id}`}>{t('Dev if No', 'Dev si No', lang)}</label>
          </div>
        </div>
      )}

      {/* Item Count List — a named, growable row list (e.g. a monthly utensil/tool count) where
          each row's count is compared against the same-named row in the previous submission */}
      {field.type === 'item_count_list' && (
        <div className="mt-1">
          <div className="text-muted mb-1" style={{ fontSize: 11 }}>
            {t('Flag a deviation when a count…', '¿Marcar desviación cuando el conteo…', lang)}
          </div>
          <div className="d-flex flex-column gap-1">
            {[
              ['any_change', t('changes at all vs. last time (Recommended)', 'cambia respecto a la vez anterior (Recomendado)', lang)],
              ['decrease_only', t('goes down vs. last time (an increase never needs a note)', 'disminuye respecto a la vez anterior (un aumento nunca requiere nota)', lang)],
              ['threshold', t('changes by more than a set amount', 'cambia más de una cantidad establecida', lang)],
            ].map(([val, label]) => (
              <div key={val} className="form-check">
                <input className="form-check-input" type="radio" id={`dm-${field.id}-${val}`} name={`dm-${field.id}`}
                  checked={(field.validation?.deviationMode || 'any_change') === val}
                  onChange={() => updateValidation('deviationMode', val)} />
                <label className="form-check-label" style={{ fontSize: 12 }} htmlFor={`dm-${field.id}-${val}`}>{label}</label>
              </div>
            ))}
          </div>
          {field.validation?.deviationMode === 'threshold' && (
            <div className="d-flex align-items-center gap-1 mt-1">
              <span className="text-muted" style={{ fontSize: 11 }}>{t('Allowed change before flagging:', 'Cambio permitido antes de marcar:', lang)}</span>
              <input type="number" min={0} className="form-control form-control-sm" style={{ width: 70 }}
                value={field.validation?.deviationThreshold ?? 0}
                onChange={e => updateValidation('deviationThreshold', Math.max(0, parseFloat(e.target.value) || 0))} />
            </div>
          )}
          <div className="form-text" style={{ fontSize: 11 }}>
            {t(
              'At fill time, the user builds a list of named items with a count each (and can add new items any time). A flagged row requires a note explaining the change before it can be submitted. Add a plain Text field after this one if you also want a general observations box.',
              'Al completar, el usuario arma una lista de artículos con un conteo cada uno (y puede agregar nuevos en cualquier momento). Una fila marcada requiere una nota que explique el cambio antes de poder enviarse. Agregue un campo de Texto después de este si también desea un cuadro de observaciones general.',
              lang
            )}
          </div>
        </div>
      )}

      {/* Visual Inspection — the criteria an AI vision call will assess per photo (default:
          weight/ingredient distribution/cooking/roundness, but editable per template). */}
      {field.type === 'visual_inspection' && (() => {
        const criteria = field.validation?.criteria || [];
        const updateCriteria = (next) => updateValidation('criteria', next);
        const updateCriterion = (idx, key, val) => {
          const next = criteria.map((c, i) => i === idx ? { ...c, [key]: val } : c);
          updateCriteria(next);
        };
        const addCriterion = () => updateCriteria([...criteria, { key: 'criterion_' + uid(), label: '' }]);
        const removeCriterion = (idx) => updateCriteria(criteria.filter((_, i) => i !== idx));
        return (
          <div className="mt-1">
            <div className="text-muted mb-1" style={{ fontSize: 11 }}>
              {t('Criteria the AI will assess from the photo:', 'Criterios que la IA evaluará a partir de la foto:', lang)}
            </div>
            {criteria.map((c, idx) => (
              <div key={idx} className="d-flex align-items-center gap-1 mb-1">
                <input className="form-control form-control-sm" style={{ maxWidth: 260 }}
                  placeholder={t('Criterion label', 'Etiqueta del criterio', lang)}
                  value={c.label || ''} onChange={e => updateCriterion(idx, 'label', e.target.value)} />
                <button className="btn btn-outline-danger btn-sm" onClick={() => removeCriterion(idx)}>×</button>
              </div>
            ))}
            <button className="btn btn-outline-secondary btn-sm" onClick={addCriterion}>
              {t('+ Add Criterion', '+ Agregar Criterio', lang)}
            </button>
            <div className="form-text" style={{ fontSize: 11 }}>
              {t(
                'At fill time, the user photographs the product (ideally on a scale) and the AI judges each criterion against the product\'s spec and reference photo (set on the Product page). QA reviews/comments on every finding before submitting. A flagged criterion requires a QA comment. Add a plain Number field after this one for the manually-measured size.',
                'Al completar, el usuario fotografía el producto (idealmente sobre una balanza) y la IA evalúa cada criterio según la especificación y la foto de referencia del producto (configurada en la página del Producto). QA revisa/comenta cada hallazgo antes de enviar. Un criterio marcado requiere un comentario de QA. Agregue un campo Número después de este para el tamaño medido manualmente.',
                lang
              )}
            </div>
          </div>
        );
      })()}

      {/* Options for select / radio / multi_check */}
      {['select', 'radio', 'multi_check'].includes(field.type) && (
        <div className="mt-2">
          <div className="text-muted mb-1" style={{ fontSize: 11 }}>{t('Options (one per line):', 'Opciones (una por línea):', lang)}</div>
          <textarea className="form-control form-control-sm" rows={3}
            value={(field.options || []).join('\n')}
            onChange={e => update('options', e.target.value.split('\n'))}
            onBlur={e => {
              const trimmed = e.target.value.split('\n').map(s => s.trim()).filter(Boolean);
              // Re-key optionRanges so keys match the trimmed option strings
              const oldRanges = field.optionRanges || {};
              const newRanges = {};
              Object.entries(oldRanges).forEach(([k, v]) => {
                const tk = k.trim();
                if (trimmed.includes(tk)) newRanges[tk] = v;
              });
              onChange({ ...field, options: trimmed, optionRanges: Object.keys(newRanges).length > 0 ? newRanges : undefined });
            }}
            placeholder={t('Option A\nOption B\nOption C', 'Opción A\nOpción B\nOpción C', lang)} />
        </div>
      )}

      {/* Per-option ranges for select / radio — used by linked number fields */}
      {['select', 'radio'].includes(field.type) && cleanedOptions.length > 0 && (
        <div className="mt-2">
          <div className="text-muted mb-1" style={{ fontSize: 11 }}>
            {t('Per-option ranges', 'Rangos por opción', lang)}{' '}
            <span className="text-muted fw-normal">
              {t(
                '(optional — link a Number field above to auto-validate against these)',
                '(opcional — vincula un campo Número arriba para validar automáticamente)',
                lang
              )}
            </span>:
          </div>
          <div className="d-flex flex-column gap-1">
            {cleanedOptions.map(opt => (
              <div key={opt} className="d-flex align-items-center gap-2">
                <span style={{ fontSize: 12, minWidth: 160, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={opt}>{opt}</span>
                <input type="number" className="form-control form-control-sm" placeholder={t('Min', 'Mínimo', lang)} style={{ width: 72 }}
                  value={field.optionRanges?.[opt]?.min ?? ''}
                  onChange={e => updateOptionRange(opt, 'min', e.target.value)} />
                <span className="text-muted" style={{ fontSize: 11 }}>–</span>
                <input type="number" className="form-control form-control-sm" placeholder={t('Max', 'Máximo', lang)} style={{ width: 72 }}
                  value={field.optionRanges?.[opt]?.max ?? ''}
                  onChange={e => updateOptionRange(opt, 'max', e.target.value)} />
                {field.unit && <span className="text-muted" style={{ fontSize: 11 }}>{field.unit}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Help text */}
      <div className="row g-2 mt-2">
        <div className="col">
          <input className="form-control form-control-sm" placeholder={t('Help text (EN, optional)', 'Texto de ayuda (EN, opcional)', lang)}
            value={field.helpText || ''} onChange={e => update('helpText', e.target.value)} />
        </div>
        <div className="col">
          <input className="form-control form-control-sm" placeholder={t('Help text (ES, optional)', 'Texto de ayuda (ES, opcional)', lang)}
            value={field.helpTextEs || ''} onChange={e => update('helpTextEs', e.target.value)} />
        </div>
      </div>
    </div>
  );
}

// ─── Schema diff ──────────────────────────────────────────────────────────────

function computeSchemaDiff(oldSchema, newSchema) {
  const lines = [];
  const oldSections = oldSchema?.sections || [];
  const newSections = newSchema?.sections || [];

  const oldSecMap = Object.fromEntries(oldSections.map(s => [s.id, s]));
  const newSecMap = Object.fromEntries(newSections.map(s => [s.id, s]));

  for (const sec of oldSections) {
    if (!newSecMap[sec.id]) lines.push(`- Section "${sec.title || '(untitled)'}" removed`);
  }

  for (const sec of newSections) {
    if (!oldSecMap[sec.id]) {
      lines.push(`+ Section "${sec.title || '(untitled)'}" added`);
      for (const f of (sec.fields || [])) lines.push(`    + Field "${f.label || '(unlabeled)'}" (${f.type}) added`);
      continue;
    }
    const oldSec = oldSecMap[sec.id];
    const sectionLines = [];
    if ((oldSec.title || '') !== (sec.title || ''))
      sectionLines.push(`  ~ Title: "${oldSec.title || ''}" → "${sec.title || ''}"`);

    const oldFieldMap = Object.fromEntries((oldSec.fields || []).map(f => [f.id, f]));
    const newFieldMap = Object.fromEntries((sec.fields || []).map(f => [f.id, f]));

    for (const f of (oldSec.fields || [])) {
      if (!newFieldMap[f.id]) sectionLines.push(`  - Field "${f.label || '(unlabeled)'}" removed`);
    }
    for (const f of (sec.fields || [])) {
      const old = oldFieldMap[f.id];
      if (!old) {
        sectionLines.push(`  + Field "${f.label || '(unlabeled)'}" (${f.type}) added`);
      } else {
        const changes = [];
        if ((old.label || '') !== (f.label || '')) changes.push(`label: "${old.label}" → "${f.label}"`);
        if (old.type !== f.type) changes.push(`type: ${old.type} → ${f.type}`);
        if (!!old.required !== !!f.required) changes.push(f.required ? 'made required' : 'made optional');
        if (changes.length > 0) sectionLines.push(`  ~ Field "${f.label || old.label}": ${changes.join(', ')}`);
      }
    }
    if (sectionLines.length > 0) {
      lines.push(`Section "${sec.title || '(untitled)'}":`);
      lines.push(...sectionLines);
    }
  }

  return lines.join('\n');
}

// ─── Publish Modal ────────────────────────────────────────────────────────────

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function PublishModal({ show, onClose, onConfirm, publishedVersions, draftSchema, publishing, error }) {
  const { lang } = useLanguage();
  const maxPublished = publishedVersions.length > 0
    ? Math.max(...publishedVersions.map(v => v.versionNumber))
    : 0;
  const isFirstRevision = maxPublished === 0;

  const [versionNum, setVersionNum] = useState('');
  const [revisionDate, setRevisionDate] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (show) {
      setVersionNum(String(maxPublished + 1));
      setRevisionDate(todayIso());

      if (isFirstRevision) {
        setNotes('');
      } else {
        const lastPublished = publishedVersions
          .filter(v => v.status === 'published' || v.status === 'archived')
          .sort((a, b) => b.versionNumber - a.versionNumber)[0];
        const diff = computeSchemaDiff(lastPublished?.schemaJson, draftSchema);
        setNotes(diff || '');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const parsed = parseInt(versionNum, 10);
  const numError = isNaN(parsed) || parsed < 1
    ? t('Enter a valid revision number.', 'Ingrese un número de revisión válido.', lang)
    : !isFirstRevision && parsed <= maxPublished
    ? t(`Must be greater than current revision (${maxPublished}).`, `Debe ser mayor que la revisión actual (${maxPublished}).`, lang)
    : '';

  const handleConfirm = () => {
    if (numError) return;
    onConfirm({ versionNumber: versionNum, revisionDate, notes });
  };

  if (!show) return null;
  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} onClick={onClose} />
      <div className="modal fade show d-block" style={{ zIndex: 1050 }} tabIndex={-1} role="dialog" aria-modal="true">
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 520 }}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" style={{ fontSize: 15 }}>{t('Publish New Revision', 'Publicar Nueva Revisión', lang)}</h5>
              <button type="button" className="btn-close" onClick={onClose} disabled={publishing} />
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2 mb-3" style={{ fontSize: 13 }}>{error}</div>}

              {!isFirstRevision && (
                <div className="alert alert-secondary py-2 mb-3" style={{ fontSize: 12 }}>
                  {t('Current published revision:', 'Revisión publicada actual:', lang)} <strong>{t('Rev', 'Rev', lang)} {maxPublished}</strong>
                  {publishedVersions.find(v => v.versionNumber === maxPublished)?.revisionDate && (
                    <span className="ms-2 text-muted">
                      ({new Date(publishedVersions.find(v => v.versionNumber === maxPublished).revisionDate + 'T00:00:00').toLocaleDateString()})
                    </span>
                  )}
                </div>
              )}

              <div className="row g-3 mb-3">
                <div className="col-sm-6">
                  <label className="form-label fw-semibold mb-0" style={{ fontSize: 13 }}>
                    {t('Revision #', 'Revisión #', lang)}
                  </label>
                  {isFirstRevision && (
                    <div className="text-muted mb-1" style={{ fontSize: 11 }}>{t('(first revision)', '(primera revisión)', lang)}</div>
                  )}
                  <input
                    type="number" min={maxPublished + 1} className={`form-control form-control-sm ${numError ? 'is-invalid' : ''}`}
                    value={versionNum}
                    onChange={e => setVersionNum(e.target.value)}
                  />
                  {numError && <div className="invalid-feedback" style={{ fontSize: 11 }}>{numError}</div>}
                  {isFirstRevision && (
                    <div className="form-text" style={{ fontSize: 11 }}>
                      {t(
                        'Set higher if migrating from a paper/legacy system.',
                        'Establezca un número mayor si está migrando desde un sistema en papel o heredado.',
                        lang
                      )}
                    </div>
                  )}
                </div>
                <div className="col-sm-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>{t('Revision Date', 'Fecha de Revisión', lang)}</label>
                  <input
                    type="date" className="form-control form-control-sm"
                    value={revisionDate}
                    onChange={e => setRevisionDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="mb-1">
                <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                  {t('Change Summary', 'Resumen de Cambios', lang)}
                  {isFirstRevision
                    ? <span className="text-muted fw-normal ms-1">{t('(optional — describe initial content)', '(opcional — describa el contenido inicial)', lang)}</span>
                    : <span className="text-muted fw-normal ms-1">{t(`(auto-detected from Rev ${maxPublished} — edit as needed)`, `(detectado automáticamente desde Rev ${maxPublished} — edite según sea necesario)`, lang)}</span>
                  }
                </label>
                <textarea
                  className="form-control form-control-sm" rows={5}
                  placeholder={isFirstRevision
                    ? t(
                        'e.g. Initial release — migrated from paper form Rev. 3 dated Jan 2024',
                        'p. ej. Lanzamiento inicial — migrado desde formulario en papel Rev. 3 de enero 2024',
                        lang
                      )
                    : t('Describe what changed in this revision…', 'Describa qué cambió en esta revisión…', lang)}
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                />
                <div className="form-text" style={{ fontSize: 11 }}>
                  {isFirstRevision
                    ? t(
                        'This summary is recorded in the audit trail and shown during inspections.',
                        'Este resumen se registra en el historial de auditoría y se muestra durante las inspecciones.',
                        lang
                      )
                    : t(
                        'Changes were detected automatically. Review and adjust the summary before publishing.',
                        'Los cambios se detectaron automáticamente. Revise y ajuste el resumen antes de publicar.',
                        lang
                      )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary btn-sm" onClick={onClose} disabled={publishing}>{t('Cancel', 'Cancelar', lang)}</button>
              <button className="btn btn-success btn-sm" onClick={handleConfirm} disabled={publishing || !!numError}>
                {publishing
                  ? <><span className="spinner-border spinner-border-sm me-1" role="status" />{t('Publishing…', 'Publicando…', lang)}</>
                  : t(`Publish Rev ${parsed > 0 ? parsed : '?'}`, `Publicar Rev ${parsed > 0 ? parsed : '?'}`, lang)}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function SqfTemplateBuilderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();

  const [template, setTemplate] = useState(null);
  const [draftVersion, setDraftVersion] = useState(null);
  const [sections, setSections] = useState([defaultSection()]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishError, setPublishError] = useState('');
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translateDone, setTranslateDone] = useState('');

  const handleTranslate = async (targetLang) => {
    if (!template?.activeVersionId) return;
    setTranslating(true);
    setTranslateDone('');
    try {
      await sqfAiService.translateEntity({ entityType: 'checklist', entityId: template.id, targetLanguage: targetLang });
      setTranslateDone(t(`Translation to ${targetLang === 'es' ? 'Spanish' : 'English'} saved as new draft.`, `Traducción al ${targetLang === 'es' ? 'español' : 'inglés'} guardada como nuevo borrador.`, lang));
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('Translation failed.', 'La traducción falló.', lang));
    } finally {
      setTranslating(false);
    }
  };

  // Template metadata editing
  const [metaOpen, setMetaOpen] = useState(false);
  const [metaName, setMetaName] = useState('');
  const [metaNameEs, setMetaNameEs] = useState('');
  const [metaCategory, setMetaCategory] = useState('OTHER');
  const [metaTrigger, setMetaTrigger] = useState('scheduled');
  const [metaSqfRef, setMetaSqfRef] = useState('');
  const [metaDesc, setMetaDesc] = useState('');
  const [savingMeta, setSavingMeta] = useState(false);
  const [savedMeta, setSavedMeta] = useState(false);

  const loadTemplate = useCallback(async () => {
    try {
      const tmpl = await sqfService.getTemplate(id);
      setTemplate(tmpl);
      setMetaName(tmpl.name || '');
      setMetaNameEs(tmpl.nameEs || '');
      setMetaCategory(tmpl.category || 'OTHER');
      setMetaTrigger(tmpl.triggerType || 'scheduled');
      setMetaSqfRef(tmpl.sqfReference || '');
      setMetaDesc(tmpl.description || '');
      const draft = (tmpl.Versions || []).find(v => v.status === 'draft');
      if (draft) {
        setDraftVersion(draft);
        setSections(draft.schemaJson?.sections || [defaultSection()]);
      } else {
        // Start from published version schema if exists
        const published = (tmpl.Versions || []).find(v => v.status === 'published');
        if (published) setSections(published.schemaJson?.sections || [defaultSection()]);
      }
    } catch {
      setError(t('Failed to load template.', 'Error al cargar la plantilla.', lang));
    } finally {
      setLoading(false);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadTemplate(); }, [loadTemplate]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const schemaJson = { sections };
      if (draftVersion) {
        await sqfService.updateVersion(id, draftVersion.id, { schemaJson });
      } else {
        const v = await sqfService.createVersion(id, { schemaJson });
        setDraftVersion(v);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      setError(t('Failed to save.', 'Error al guardar.', lang));
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = () => {
    if (!draftVersion) {
      setError(t('Save the draft first before publishing.', 'Guarde el borrador primero antes de publicar.', lang));
      return;
    }
    setPublishError('');
    setShowPublishModal(true);
  };

  const handlePublishConfirm = async ({ versionNumber, revisionDate, notes }) => {
    setPublishing(true);
    setPublishError('');
    try {
      await sqfService.publishVersion(id, draftVersion.id, { versionNumber, revisionDate, notes });
      setShowPublishModal(false);
      navigate(`/sqf/templates`);
    } catch (err) {
      setPublishError(err?.response?.data?.message || t('Failed to publish version.', 'Error al publicar la versión.', lang));
    } finally {
      setPublishing(false);
    }
  };

  const [showDeleteDraftConfirm, setShowDeleteDraftConfirm] = useState(false);
  const [deletingDraft, setDeletingDraft] = useState(false);

  const handleDeleteDraft = async () => {
    if (!draftVersion) return;
    setDeletingDraft(true);
    try {
      await sqfService.deleteVersion(id, draftVersion.id);
      setShowDeleteDraftConfirm(false);
      setDraftVersion(null);
      const published = (template?.Versions || []).find(v => v.status === 'published');
      setSections(published?.schemaJson?.sections || [defaultSection()]);
    } catch {
      setError(t('Failed to delete draft.', 'Error al eliminar el borrador.', lang));
    } finally {
      setDeletingDraft(false);
    }
  };

  const handleSaveMeta = async () => {
    if (!metaName.trim()) return;
    setSavingMeta(true);
    try {
      const updated = await sqfService.updateTemplate(id, {
        name: metaName.trim(),
        nameEs: metaNameEs.trim() || null,
        category: metaCategory,
        triggerType: metaTrigger,
        sqfReference: metaSqfRef || null,
        description: metaDesc || null,
      });
      setTemplate(prev => ({ ...prev, ...updated }));
      setSavedMeta(true);
      setTimeout(() => setSavedMeta(false), 2000);
    } catch {
      setError(t('Failed to save template settings.', 'Error al guardar la configuración de la plantilla.', lang));
    } finally {
      setSavingMeta(false);
    }
  };

  const addSection = () => setSections(s => [...s, defaultSection()]);

  const updateSection = (idx, updated) => setSections(s => s.map((sec, i) => i === idx ? updated : sec));

  const deleteSection = (idx) => setSections(s => s.filter((_, i) => i !== idx));

  const addField = (sectionIdx) => {
    setSections(s => s.map((sec, i) => i === sectionIdx
      ? { ...sec, fields: [...(sec.fields || []), defaultField()] }
      : sec
    ));
  };

  const updateField = (sectionIdx, fieldIdx, updated) => {
    setSections(s => s.map((sec, i) => i === sectionIdx
      ? { ...sec, fields: sec.fields.map((f, j) => j === fieldIdx ? updated : f) }
      : sec
    ));
  };

  const deleteField = (sectionIdx, fieldIdx) => {
    setSections(s => s.map((sec, i) => i === sectionIdx
      ? { ...sec, fields: sec.fields.filter((_, j) => j !== fieldIdx) }
      : sec
    ));
  };

  if (loading) return (
    <div className="container mt-4 text-center py-5 text-muted">
      <div className="spinner-border spinner-border-sm me-2" />{t('Loading…', 'Cargando…', lang)}
    </div>
  );

  return (
    <div className="container-fluid mt-3 pb-5" style={{ maxWidth: 860 }}>
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <Link to="/sqf/templates" className="text-muted" style={{ fontSize: 12 }}>← {t('Templates', 'Plantillas', lang)}</Link>
          <h4 className="mb-0 mt-1">{template?.name}</h4>
          {draftVersion && <span className="badge bg-secondary" style={{ fontSize: 10 }}>{t('Draft', 'Borrador', lang)} v{draftVersion.versionNumber}</span>}
        </div>
        <div className="d-flex gap-2 flex-wrap">
          {template?.activeVersionId && (
            <Link to={`/sqf/templates/${id}/audit`} className="btn btn-outline-secondary btn-sm">{t('Audit Log', 'Historial de Auditoría', lang)}</Link>
          )}
          {template?.activeVersionId && (
            <div className="dropdown">
              <button className="btn btn-outline-primary btn-sm dropdown-toggle" data-bs-toggle="dropdown" disabled={translating}>
                {translating ? <><span className="spinner-border spinner-border-sm me-1" />t('Translating…', 'Traduciendo…', lang)</> : `✨ ${t('Translate', 'Traducir', lang)}`}
              </button>
              <ul className="dropdown-menu dropdown-menu-end">
                <li><button className="dropdown-item" onClick={() => handleTranslate('es')}>→ {t('Spanish', 'Español', lang)}</button></li>
                <li><button className="dropdown-item" onClick={() => handleTranslate('en')}>→ {t('English', 'Inglés', lang)}</button></li>
              </ul>
            </div>
          )}
          {draftVersion && (
            <button className="btn btn-outline-danger btn-sm" onClick={() => setShowDeleteDraftConfirm(true)}>
              🗑️ {t('Delete Draft', 'Eliminar Borrador', lang)}
            </button>
          )}
          <button className="btn btn-outline-secondary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? t('Saving…', 'Guardando…', lang) : saved ? `✓ ${t('Saved', 'Guardado', lang)}` : t('Save Draft', 'Guardar Borrador', lang)}
          </button>
          <button className="btn btn-success btn-sm" onClick={handlePublish} disabled={publishing || !draftVersion}>
            {publishing ? t('Publishing…', 'Publicando…', lang) : t('Publish', 'Publicar', lang)}
          </button>
        </div>
      </div>

      <ConfirmModal
        show={showDeleteDraftConfirm}
        title={t('Delete Draft', 'Eliminar Borrador', lang)}
        message={draftVersion && t(
          `Delete draft v${draftVersion.versionNumber}? All unsaved changes will be permanently removed. This cannot be undone.`,
          `¿Eliminar el borrador v${draftVersion.versionNumber}? Todos los cambios no guardados se eliminarán permanentemente. Esta acción no se puede deshacer.`,
          lang
        )}
        confirmLabel={deletingDraft ? t('Deleting…', 'Eliminando…', lang) : t('Delete Draft', 'Eliminar Borrador', lang)}
        variant="danger"
        onConfirm={handleDeleteDraft}
        onCancel={() => setShowDeleteDraftConfirm(false)}
      />

      {error && <div className="alert alert-danger py-2 mb-3">{error}</div>}
      {translateDone && <div className="alert alert-success py-2 mb-3 d-flex align-items-center gap-2">{translateDone} <button className="btn-close btn-sm ms-auto" onClick={() => setTranslateDone('')} /></div>}

      {/* Template settings panel */}
      <div className="card mb-3 border-secondary border-opacity-25">
        <div
          className="card-header py-2 d-flex align-items-center justify-content-between"
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => setMetaOpen(o => !o)}
        >
          <div className="d-flex align-items-center gap-2">
            <span style={{ fontSize: 13 }}>⚙ {t('Template Settings', 'Configuración de Plantilla', lang)}</span>
            <span className="badge bg-secondary bg-opacity-25 text-secondary border" style={{ fontSize: 10 }}>
              {TRIGGER_LABELS[template?.triggerType] || template?.triggerType || 'scheduled'}
            </span>
            {template?.category && template.category !== 'OTHER' && (
              <span className="badge bg-info bg-opacity-25 text-info border border-info border-opacity-25" style={{ fontSize: 10 }}>
                {template.category}
              </span>
            )}
          </div>
          <span className="text-muted" style={{ fontSize: 12 }}>{metaOpen ? `▲ ${t('Hide', 'Ocultar', lang)}` : `▼ ${t('Edit', 'Editar', lang)}`}</span>
        </div>
        {metaOpen && (
          <div className="card-body py-3">
            <div className="row g-3 mb-3">
              <div className="col-sm-6">
                <label className="form-label fw-semibold" style={{ fontSize: 13 }}>{t('Name (EN)', 'Nombre (EN)', lang)}</label>
                <input className="form-control form-control-sm" value={metaName} onChange={e => setMetaName(e.target.value)} />
              </div>
              <div className="col-sm-6">
                <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                  {t('Name (ES)', 'Nombre (ES)', lang)} <span className="text-muted fw-normal">{t('(optional)', '(opcional)', lang)}</span>
                </label>
                <input className="form-control form-control-sm" placeholder={t('Spanish name…', 'Nombre en español…', lang)} value={metaNameEs} onChange={e => setMetaNameEs(e.target.value)} />
              </div>
            </div>
            <div className="row g-3 mb-3">
              <div className="col-sm-6">
                <label className="form-label fw-semibold" style={{ fontSize: 13 }}>{t('Category', 'Categoría', lang)}</label>
                <select className="form-select form-select-sm" value={metaCategory} onChange={e => setMetaCategory(e.target.value)}>
                  {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="col-sm-6">
                <label className="form-label fw-semibold" style={{ fontSize: 13 }}>{t('Trigger Type', 'Tipo de Activación', lang)}</label>
                <select className="form-select form-select-sm" value={metaTrigger} onChange={e => setMetaTrigger(e.target.value)}>
                  {Object.entries(TRIGGER_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                {metaTrigger === 'before_production' && (
                  <div className="form-text text-danger" style={{ fontSize: 11 }}>
                    {t(
                      'Gates the baking page — must be completed before production starts each day.',
                      'Bloquea la página de producción — debe completarse antes de que comience la producción cada día.',
                      lang
                    )}
                  </div>
                )}
                {metaTrigger === 'after_production' && (
                  <div className="form-text text-warning" style={{ fontSize: 11 }}>
                    {t(
                      'Triggered when a production run is completed — shown as a banner to complete before closing the shift.',
                      'Se activa cuando se completa una corrida de producción — se muestra como un aviso para completar antes de cerrar el turno.',
                      lang
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="row g-3 mb-3">
              <div className="col-sm-12">
                <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                  {t('SQF Reference', 'Referencia SQF', lang)} <span className="text-muted fw-normal">{t('(optional)', '(opcional)', lang)}</span>
                </label>
                <input className="form-control form-control-sm" placeholder={t('e.g. DOC REF F006 · SQF REF 11.2.5', 'p. ej. DOC REF F006 · SQF REF 11.2.5', lang)} value={metaSqfRef} onChange={e => setMetaSqfRef(e.target.value)} />
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold" style={{ fontSize: 13 }}>{t('Description', 'Descripción', lang)}</label>
              <textarea className="form-control form-control-sm" rows={2} value={metaDesc} onChange={e => setMetaDesc(e.target.value)} />
            </div>
            <button className="btn btn-primary btn-sm" onClick={handleSaveMeta} disabled={savingMeta || !metaName.trim()}>
              {savingMeta ? t('Saving…', 'Guardando…', lang) : savedMeta ? `✓ ${t('Saved', 'Guardado', lang)}` : t('Save Settings', 'Guardar Configuración', lang)}
            </button>
          </div>
        )}
      </div>

      {sections.map((section, sIdx) => (
        <div key={section.id} className="card mb-3 shadow-sm">
          <div className="card-header py-2 d-flex align-items-center gap-2">
            <input className="form-control form-control-sm" placeholder={t('Section title (EN, optional)', 'Título de sección (EN, opcional)', lang)}
              value={section.title || ''} onChange={e => updateSection(sIdx, { ...section, title: e.target.value })}
              style={{ maxWidth: 260 }} />
            <input className="form-control form-control-sm" placeholder={t('Section title (ES, optional)', 'Título de sección (ES, opcional)', lang)}
              value={section.titleEs || ''} onChange={e => updateSection(sIdx, { ...section, titleEs: e.target.value })}
              style={{ maxWidth: 260 }} />
            <button className="btn btn-outline-danger btn-sm ms-auto" onClick={() => deleteSection(sIdx)}
              disabled={sections.length === 1}>{t('Remove Section', 'Eliminar Sección', lang)}</button>
          </div>
          <div className="card-body py-2 px-3">
            {(section.fields || []).map((field, fIdx) => {
              const selectableFields = sections
                .flatMap(s => s.fields || [])
                .filter(f => (f.type === 'select' || f.type === 'radio') && f.id !== field.id && f.label);
              return (
                <FieldEditor key={field.id} field={field}
                  onChange={updated => updateField(sIdx, fIdx, updated)}
                  onDelete={() => deleteField(sIdx, fIdx)}
                  selectableFields={selectableFields} />
              );
            })}
            <button className="btn btn-outline-secondary btn-sm mt-1" onClick={() => addField(sIdx)}>+ {t('Add Field', 'Agregar Campo', lang)}</button>
          </div>
        </div>
      ))}

      <button className="btn btn-outline-primary btn-sm" onClick={addSection}>+ {t('Add Section', 'Agregar Sección', lang)}</button>

      {/* ── Revision History ─────────────────────────────────────────────────── */}
      {(() => {
        const allVersions = (template?.Versions || [])
          .filter(v => v.status !== 'draft')
          .sort((a, b) => b.versionNumber - a.versionNumber);
        if (allVersions.length === 0) return null;
        return (
          <div className="card mt-4 border-secondary border-opacity-25">
            <div className="card-header py-2">
              <span style={{ fontSize: 13 }}>📋 {t('Revision History', 'Historial de Revisiones', lang)}</span>
              <span className="text-muted ms-2" style={{ fontSize: 11 }}>— {t('audit trail for inspections', 'historial de auditoría para inspecciones', lang)}</span>
            </div>
            <div className="card-body p-0">
              <table className="table table-sm table-hover mb-0" style={{ fontSize: 13 }}>
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 80 }}>{t('Rev #', 'Rev #', lang)}</th>
                    <th style={{ width: 110 }}>{t('Date', 'Fecha', lang)}</th>
                    <th style={{ width: 80 }}>{t('Status', 'Estado', lang)}</th>
                    <th>{t('Change Summary', 'Resumen de Cambios', lang)}</th>
                    <th style={{ width: 130 }}>{t('Published By', 'Publicado Por', lang)}</th>
                    <th style={{ width: 110 }}>{t('Published At', 'Publicado En', lang)}</th>
                  </tr>
                </thead>
                <tbody>
                  {allVersions.map(v => (
                    <tr key={v.id}>
                      <td className="fw-semibold">{t('Rev', 'Rev', lang)} {v.versionNumber}</td>
                      <td className="text-muted">
                        {v.revisionDate
                          ? new Date(v.revisionDate + 'T00:00:00').toLocaleDateString()
                          : '—'}
                      </td>
                      <td>
                        {v.status === 'published'
                          ? <span className="badge bg-success">{t('Active', 'Activo', lang)}</span>
                          : <span className="badge bg-secondary">{t('Archived', 'Archivado', lang)}</span>}
                      </td>
                      <td className="text-muted" style={{ whiteSpace: 'pre-wrap' }}>{v.notes || '—'}</td>
                      <td className="text-muted">
                        {v.PublishedBy
                          ? (v.PublishedBy.firstName || v.PublishedBy.username)
                          : '—'}
                      </td>
                      <td className="text-muted">
                        {v.publishedAt ? new Date(v.publishedAt).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      <PublishModal
        show={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        onConfirm={handlePublishConfirm}
        publishedVersions={(template?.Versions || []).filter(v => v.status === 'published' || v.status === 'archived')}
        draftSchema={{ sections }}
        publishing={publishing}
        error={publishError}
      />
    </div>
  );
}
